import React, { useState } from 'react';
import styled from 'styled-components';
import { Tooltip, Popover, Dropdown } from 'antd';
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, SparklesIcon, XCircleIcon, PlayIcon, ExclamationIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { colors } from '../../design-system/tokens';
import { workflowAIService, type WorkflowSuggestion } from '../../services/workflowAIService';

// Updated status type to match table workflow stages
export type WorkflowStatus = 'not_started' | 'in_progress' | 'completed' | 'skipped';

export interface PendingStatusChange {
  value: WorkflowStatus;
  reasoning?: string;
  proposed_at: string;
  proposed_by: 'user' | 'ai';
}

interface WorkflowStatusColumnProps {
  creatorId: string;
  currentStatus: WorkflowStatus;
  pendingStatus?: PendingStatusChange;
  onStatusChange?: (creatorId: string, newStatus: WorkflowStatus) => void;
  onAcceptPending?: (creatorId: string) => void;
  onDismissPending?: (creatorId: string) => void;
  disabled?: boolean;
  enableAISuggestions?: boolean;
  aiSuggestion?: WorkflowSuggestion;
  onRequestAISuggestion?: (creatorId: string) => void;
}

const StatusContainer = styled.div<{ $status: WorkflowStatus; $hasPending?: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  user-select: none;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s ease;
  
  ${props => {
    // Subtle Stripe-style colors
    const getStatusColors = () => {
      if (props.$hasPending) {
        return {
          bg: '#fef3c7',
          color: '#92400e',
          hoverBg: '#fde68a'
        };
      }
      
      switch (props.$status) {
        case 'not_started':
          return {
            bg: '#f3f4f6',
            color: '#6b7280',
            hoverBg: '#e5e7eb'
          };
        case 'in_progress':
          return {
            bg: '#dbeafe',
            color: '#1e40af',
            hoverBg: '#bfdbfe'
          };
        case 'completed':
          return {
            bg: '#d1fae5',
            color: '#065f46',
            hoverBg: '#a7f3d0'
          };
        case 'skipped':
          return {
            bg: '#f3f4f6',
            color: '#9ca3af',
            hoverBg: '#e5e7eb'
          };
        default:
          return {
            bg: '#f3f4f6',
            color: '#6b7280',
            hoverBg: '#e5e7eb'
          };
      }
    };
    
    const statusColors = getStatusColors();
    
    return `
      background: ${statusColors.bg};
      color: ${statusColors.color};
      
      &:hover {
        background: ${props.$disabled ? statusColors.bg : statusColors.hoverBg};
      }
    `;
  }}
`;

const StatusContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const StatusIcon = styled.div<{ $status: WorkflowStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: inherit;
  opacity: 0.7;
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

const StatusText = styled.span<{ $status: WorkflowStatus }>`
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  white-space: nowrap;
`;

const PendingIcon = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: ${colors.palette.amber[100]};
  border: 1px solid ${colors.palette.amber[200]};
  border-radius: 50%;
  padding: 2px;
  cursor: pointer;
  z-index: 10;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.palette.amber[200]};
    border-color: ${colors.palette.amber[300]};
  }
  
  svg {
    width: 12px;
    height: 12px;
    color: ${colors.palette.amber[600]};
  }
`;

const WORKFLOW_STATUSES: Array<{
  key: WorkflowStatus;
  label: string;
  icon: React.ReactNode;
  description: string;
}> = [
  {
    key: 'not_started',
    label: 'Not Started',
    icon: <ClockIcon />,
    description: 'Creator not yet contacted'
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    icon: <PlayIcon />,
    description: 'Actively working with creator'
  },
  {
    key: 'completed',
    label: 'Completed',
    icon: <CheckCircleIcon />,
    description: 'Stage completed successfully'
  },
  {
    key: 'skipped',
    label: 'Skipped',
    icon: <XCircleIcon />,
    description: 'Skipped this stage'
  }
];

export const WorkflowStatusColumn: React.FC<WorkflowStatusColumnProps> = ({
  creatorId,
  currentStatus,
  pendingStatus,
  onStatusChange,
  onAcceptPending,
  onDismissPending,
  disabled = false,
  enableAISuggestions = false,
  aiSuggestion,
  onRequestAISuggestion
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingPopoverOpen, setPendingPopoverOpen] = useState(false);
  
  const currentStatusConfig = WORKFLOW_STATUSES.find(s => s.key === currentStatus) || WORKFLOW_STATUSES[0];
  const pendingStatusConfig = pendingStatus ? WORKFLOW_STATUSES.find(s => s.key === pendingStatus.value) : null;
  const displayStatus = pendingStatus?.value || currentStatus;
  const displayStatusConfig = WORKFLOW_STATUSES.find(s => s.key === displayStatus) || WORKFLOW_STATUSES[0];
  
  const handleStatusChange = (newStatus: WorkflowStatus) => {
    if (!disabled && onStatusChange) {
      console.log('🔄 [WorkflowStatus] Manually changing status:', creatorId, currentStatus, '->', newStatus);
      onStatusChange(creatorId, newStatus);
      setDropdownOpen(false);
    }
  };
  
  const handleAcceptPending = () => {
    if (onAcceptPending) {
      onAcceptPending(creatorId);
      setPendingPopoverOpen(false);
    }
  };
  
  const handleDismissPending = () => {
    if (onDismissPending) {
      onDismissPending(creatorId);
      setPendingPopoverOpen(false);
    }
  };
  
  // Create dropdown menu items
  const menuItems = WORKFLOW_STATUSES.map(status => {
    const isAISuggested = aiSuggestion?.suggestedStatus === status.key;
    
    return {
      key: status.key,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', transition: 'background-color 0.15s ease', background: isAISuggested ? colors.palette.indigo[50] : 'transparent', border: isAISuggested ? `1px solid ${colors.palette.indigo[200]}` : '1px solid transparent', borderRadius: '4px', margin: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '12px', height: '12px', color: 'inherit', opacity: 0.7 }}>
            {status.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: 500 }}>{status.label}</span>
              {isAISuggested && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: colors.palette.indigo[600], fontWeight: 500 }}>
                  <SparklesIcon style={{ width: '12px', height: '12px' }} />
                  AI
                </div>
              )}
            </div>
            <div style={{ fontSize: '10px', color: colors.text.tertiary, marginTop: '2px' }}>
              {status.description}
            </div>
            {isAISuggested && aiSuggestion && (
              <div style={{ fontSize: '10px', color: colors.text.secondary, marginTop: '2px', lineHeight: 1.3 }}>
                {aiSuggestion.reasoning}
              </div>
            )}
          </div>
        </div>
      ),
      onClick: () => handleStatusChange(status.key)
    };
  });
  
  // Pending change popover content
  const pendingChangeContent = pendingStatus ? (
    <div style={{ width: '360px', padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: colors.text.primary, 
        padding: '12px 16px', 
        borderBottom: `1px solid ${colors.border.secondary}`, 
        flexShrink: 0 
      }}>
        {pendingStatus.proposed_by === 'ai' ? 'AI Suggests Status Update' : 'Pending Status Change'}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', maxHeight: '400px' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.text.primary, marginBottom: '8px' }}>
            Current Status
          </div>
          <div style={{ 
            background: '#f8f9fa', 
            borderRadius: '6px', 
            padding: '8px 10px', 
            fontSize: '12px', 
            color: colors.text.primary, 
            lineHeight: 1.5 
          }}>
            {currentStatusConfig.label}
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.text.primary, marginBottom: '8px' }}>
            New Status
          </div>
          <div style={{ 
            background: 'rgba(251, 191, 36, 0.08)', 
            border: '1px solid rgba(251, 191, 36, 0.3)', 
            borderRadius: '6px', 
            padding: '8px 10px', 
            fontSize: '12px', 
            color: colors.text.primary, 
            lineHeight: 1.5 
          }}>
            {pendingStatusConfig?.label || pendingStatus.value}
          </div>
        </div>

        {pendingStatus.reasoning && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, color: colors.text.primary, marginBottom: '8px' }}>
              Reasoning
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: colors.text.secondary, 
              lineHeight: 1.5, 
              padding: '10px 12px', 
              background: 'rgba(59, 130, 246, 0.05)', 
              border: '1px solid rgba(59, 130, 246, 0.15)', 
              borderRadius: '6px' 
            }}>
              {pendingStatus.reasoning}
            </div>
          </div>
        )}
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: '8px', 
        padding: '12px 16px', 
        borderTop: `1px solid ${colors.border.secondary}`, 
        flexShrink: 0 
      }}>
        <button 
          style={{
            background: 'transparent',
            color: colors.text.secondary,
            padding: '6px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.2s'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleDismissPending();
          }}
        >
          Dismiss
        </button>
        <button 
          style={{
            background: colors.brand.primary,
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.2s'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleAcceptPending();
          }}
        >
          Accept
        </button>
      </div>
    </div>
  ) : null;
  
  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      open={dropdownOpen}
      onOpenChange={(open) => {
        // Don't open dropdown if clicking the pending icon
        if (!pendingPopoverOpen) {
          setDropdownOpen(open);
        }
      }}
      placement="bottomLeft"
      overlayStyle={{ zIndex: 1100 }}
      disabled={disabled}
    >
      <StatusContainer $status={displayStatus} $hasPending={!!pendingStatus} $disabled={disabled}>
        <StatusContent>
          <StatusIcon $status={displayStatus}>
            {displayStatusConfig.icon}
          </StatusIcon>
          <StatusText $status={displayStatus}>
            {displayStatusConfig.label}
          </StatusText>
        </StatusContent>
        
        {!disabled && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '10px', height: '10px', color: 'inherit', opacity: 0.6, marginLeft: '2px' }}>
            <ChevronDownIcon style={{ width: '8px', height: '8px' }} />
          </div>
        )}
        
        {pendingStatus && (
          <Popover
            content={pendingChangeContent}
            trigger="click"
            placement="bottomLeft"
            overlayStyle={{ zIndex: 1100 }}
            open={pendingPopoverOpen}
            onOpenChange={setPendingPopoverOpen}
          >
            <PendingIcon>
              <ClockIcon />
            </PendingIcon>
          </Popover>
        )}
      </StatusContainer>
    </Dropdown>
  );
};